import styled from 'styled-components';
import { type ThemeConfig, useTheme } from '../../../theme';
import type { MetricBlock as MetricBlockType } from '../../../types/blocks';

interface MetricBlockProps {
  block: MetricBlockType;
}

const MetricBlock: React.FC<MetricBlockProps> = ({ block }) => {
  const theme = useTheme();

  const formatValue = (value: number, format?: string, currency?: string, precision?: number) => {
    const defaultPrecision = precision ?? 2;

    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency || 'USD',
          minimumFractionDigits: defaultPrecision,
          maximumFractionDigits: defaultPrecision
        }).format(value);

      case 'percentage':
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: defaultPrecision,
          maximumFractionDigits: defaultPrecision
        }).format(value / 100);

      case 'decimal':
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: defaultPrecision,
          maximumFractionDigits: defaultPrecision
        }).format(value);

      case 'number':
      default:
        return new Intl.NumberFormat('en-US').format(value);
    }
  };

  return (
    <MetricContainer theme={theme}>
      <MetricValue>
        {formatValue(block.value, block.format, block.currency, block.precision)}
      </MetricValue>
      <MetricLabel>{block.label}</MetricLabel>
    </MetricContainer>
  );
};

const MetricContainer = styled.div<{ theme: ThemeConfig }>`
  margin-top: 16px;
  padding: 24px;
  border-radius: 12px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#ffffff')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
      : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'};
  text-align: center;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 8px 25px -1px rgba(0, 0, 0, 0.4), 0 4px 6px -1px rgba(0, 0, 0, 0.3)'
        : '0 8px 25px -1px rgba(0, 0, 0, 0.15), 0 4px 6px -1px rgba(0, 0, 0, 0.1)'};
  }
`;

const MetricValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #3b82f6;
  line-height: 1.1;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const MetricLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TTSButtonContainer = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

export default MetricBlock;
